// frontend/src/lib/api.ts

// ---------- Legacy workflow facade (unchanged) ----------
type WFModule = Record<string, any>;
let wfModPromise: Promise<WFModule> | null = null;

async function loadWF(): Promise<WFModule> {
  if (!wfModPromise) {
    // Adjust this path if your legacy workflow UI exports its API from another file
    wfModPromise = import("@/features/workflow/src_raw/ui/api").then(
      (mod: any) => mod?.default ?? mod ?? {}
    );
  }
  return wfModPromise;
}

async function pickFn(...names: string[]) {
  const wf = await load();
  for (const n of names) {
    const f = (wf as any)[n];
    if (typeof f === "function") return f.bind(wf);
  }
  throw new Error(
    `Workflow API facade: none of [${names.join(", ")}] exist on src_raw/ui/api`
  );
}

export async function listWorkflowSpecs(...args: any[]) {
  return (await pickFn("listWorkflowSpecs", "listSpecs", "list"))(...args);
}

export async function createWorkflowSpec(...args: any[]) {
  return (await pickFn("createWorkflowSpec", "createSpec", "create"))(...args);
}

export async function getWorkflowSpec(...args: any[]) {
  return (await pickFn("getWorkflowSpec", "getSpec", "get"))(...args);
}

export async function updateWorkflowSpec(...args: any[]) {
  return (await pickFn("updateWorkflowSpec", "updateSpec", "update"))(...args);
}

export async function deleteWorkflowSpec(...args: any[]) {
  return (await pickFn("deleteWorkflowSpec", "deleteSpec", "remove", "del"))(
    ...args
  );
}

export async function createRun(...args: any[]) {
  return (await pickFn("createRun", "run", "createWorkflowRun"))(...args);
}

export * from "@/features/workflow/src_raw/lib/types";

// ---------- Safe API base + helpers ----------

const RAW_BASE =
  (import.meta as any)?.env?.VITE_API_BASE !== undefined
    ? (import.meta as any).env.VITE_API_BASE
    : "/v1";

/** Normalize API base to a proper URL prefix. */
function computeApiBase(): string {
  const raw = typeof RAW_BASE === "string" ? RAW_BASE : "/v1";
  try {
    const u = new URL(raw, typeof window !== "undefined" ? window.location.origin : "http://localhost");
    return u.toString().replace(/\/$/, "");
  } catch {
    return "/v1";
  }
}

const API_BASE = computeApiBase();

function buildUrl(path: string): string {
  if (/^https?:\/\//i.test(path)) return path;
  const suffix = path.startsWith("/") ? path : `/${path}`;
  return `${API_BASE}${suffix}`;
}

/**
 * Low-level fetch that never throws for non-2xx (caller can inspect res.ok).
 * Keeps credentials included (cookies) — adjust if you use pure bearer tokens.
 */
export async function api(
  path: string,
  init?: RequestInit
): Promise<Response> {
  const url = buildUrl(path);
  return fetch(url, { credentials: "include", ...init });
}

/**
 * High-level JSON helper that throws on non-2xx and parses JSON.
 */
export async function apiJson<T = any>(
  path: string,
  init?: RequestInit
): Promise<T> {
  const res = await api(path, {
    ...init,
    headers: {
      "Content-Type": "application/json",
      ...(init?.headers || {}),
    },
  });
  if (!res.ok) {
    const text = await res.text().catch(() => "");
    throw new Error(`HTTP ${res.status}: ${text || res.statusText}`);
  }
  return (await res.json()) as T;
}

// ---------- Feedback types + API (new DAG-backed) ----------

export type FeedbackLabel = "positive" | "negative" | "neutral";

export interface FeedbackRow {
  id: string;
  tenant_id: string;
  run_id: string | null;
  question?: string | null;
  answer?: string | null;
  label: FeedbackLabel | null;
  comment?: string | null;
  created_at: string;
  updated_at?: string | null;
}

export interface ListFeedbackParams {
  tenant_id: string;
  limit?: number;
  offset?: number;
}

export interface UpdateFeedbackInput {
  id: string;
  tenant_id: string;
  label?: FeedbackLabel;
  comment?: string | null;
}

/** GET /v1/feedback?tenant_id=...&limit=...&offset=... */
export async function listFeedback(
  params: ListFeedbackParams
): Promise<FeedbackRow[]> {
  const qs = new URLSearchParams();
  if (params.tenant_id) qs.set("tenant_id", params.tenant_id);
  if (params.limit != null) qs.set("limit", String(params.limit));
  if (params.offset != null) qs.set("offset", String(params.offset));

  const res = await api(`/v1/feedback?${qs.toString()}`, {
    method: "GET",
  });

  if (!res.ok) {
    const text = await res.text().catch(() => "");
    throw new Error(`listFeedback failed: ${res.status} ${text}`);
  }

  return (await res.json()) as FeedbackRow[];
}

/** PATCH /v1/feedback/{id} */
export async function updateFeedback(
  input: UpdateFeedbackInput
): Promise<FeedbackRow> {
  const { id, ...body } = input;
  const res = await api(`/v1/feedback/${encodeURIComponent(id)}`, {
    method: "PATCH",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify(body),
  });

  if (!res.ok) {
    const text = await res.text().catch(() => "");
    throw new Error(`updateFeedback failed: ${res.status} ${text}`);
  }

  return (await res.json()) as FeedbackRow;
}

// ---------- Misc existing helpers (unchanged, just cleaned up) ----------

export async function getJSON(path: string) {
  const base = (import.meta as any).env.VITE_API_BASE || "http://localhost:8001";
  const res = await fetch(base + path);
  if (!res.ok) {
    throw new Error(await res.text());
  }
  return res.json();
}

export function wsRun(runId: string): WebSocket {
  const base = (import.meta as any).env.VITE_API_BASE || "http://localhost:8001";
  return new WebSocket(`${base.replace("http", "ws")}/ws/runs/${runId}`);
}

export async function getPresigned(runId: string, nodeId: string) {
  const base = (import.meta as any).env.VITE_API_BASE || "http://localhost:8001";
  const res = await fetch(`${base}/v1/runs/${runId}/artifacts/${nodeId}`);
  if (!res.ok) {
    throw new Error(await res.text());
  }
  return res.json();
}

function randomHex(n: number) {
  return Array.from(crypto.getRandomValues(new Uint8Array(n)))
    .map((b) => b.toString(16).padStart(2, "0"))
    .join("");
}

export function traceHeaders() {
  const traceId = randomHex(16);
  const parentId = randomHex(8);
  return { traceparent: `00${traceId}-${parentId}-01` };
}

export type Role = "viewer" | "operator" | "editor" | "admin";

export function currentRole(): Role {
  try {
    const t = localStorage.getItem("jwt");
    if (!t) return "viewer";
    const p = JSON.parse(atob(t.split(".")[1] ?? ""));
    return (p.role as Role) || "viewer";
  } catch {
    return "viewer";
  }
}

export type Json = any;

export class ApiError extends Error {
  status: number;
  body: any;
  constructor(message: string, status: number, body: any) {
    super(message);
    this.status = status;
    this.body = body;
  }
}

async function parseJsonSafe(res: Response) {
  const text = await res.text();
  if (!text) return null;
  try { return JSON.parse(text); } catch { return text; }
}

export async function setIngressAppAuthMode(appId: string, tenantId: string, authMode: "tenant" | "api_key") {
  return apiJson(`/v1/admin/ingress/apps/${encodeURIComponent(appId)}/set_auth_mode?tenant_id=${encodeURIComponent(tenantId)}`, {
    method: "POST",
    body: JSON.stringify({ auth_mode: authMode }),
  });
}
